import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faWallet,
  faUsers,
  faIndianRupee,
  faBookOpen,
  faArrowRight,
  faCopy,
  faChartLine,
  faMoneyBillWave,
  faUserPlus,
  faSitemap,
  faFileInvoice,
  faShoppingCart,
  faTimesCircle,
  faCheckCircle,
  faHourglassHalf,
  faIdCard,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import {
  Row,
  Col,
  Button,
  Table,
  Badge,
  OverlayTrigger,
  Tooltip,
} from "@themesberg/react-bootstrap";

import { Routes } from "../../../routes";
import {
  userDashboardData,
  userProfileData,
  userMyCourses,
  userWalletData,
  userMyReferralList,
} from "../../../pages/api";
import DashboardStatCard from "../../../components/dashboard/DashboardStatCard";
import DashboardSection from "../../../components/dashboard/DashboardSection";
import EmptyState from "../../../components/dashboard/EmptyState";
import LoadingSkeleton from "../../../components/dashboard/LoadingSkeleton";
import {
  ACTIVE_BUTTON_STYLE,
  formatCurrency,
  capitalize,
  formatDate,
  getStoredUser,
  getPaymentStatusBadge,
  getCourseStatusBadge,
  getCourseStatusLabel,
  getActivityBadge,
  buildReferralLink,
} from "../../../components/dashboard/dashboardHelpers";

const getCourseAccessPath = () =>
  Routes?.UserPurchaseCourseList?.path || "/user-purchase-course-list";

const mapTransaction = (item) => ({
  id: item?.id ?? item?.transaction_id ?? Date.now(),
  type: String(item?.type || "—").toLowerCase(),
  amount: Number(item?.amount || 0),
  status: String(item?.status || "—").toLowerCase(),
  payment_method: capitalize(item?.payment_method || "—"),
  created_at: item?.created_at || null,
});

const mapCourse = (item) => ({
  id: item?.id,
  title: item?.title || "—",
  status: item?.status || "—",
  opt_out_status: Boolean(item?.opt_out_status),
  can_toggle: item?.can_toggle ?? false,
  is_final: item?.is_final ?? false,
  status_label: item?.status_label || "",
  opt_out_deadline: item?.opt_out_deadline || null,
  created_at: item?.created_at || null,
  month_year: item?.month_year || null,
});

const mapReferral = (item) => ({
  id: item?.id,
  name: item?.name || "—",
  email: item?.email || "—",
  status: item?.status || "—",
  created_at: item?.created_at || null,
});

const InfoTip = ({ label, tip }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip>{tip}</Tooltip>}>
    <span className="dashboard-info-tip ms-1">{label}</span>
  </OverlayTrigger>
);

export default function UserDashboard() {
  const storedUser = getStoredUser();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [referralsCount, setReferralsCount] = useState(0);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [walletAmount, setWalletAmount] = useState(0);
  const [pendingWithdrawal, setPendingWithdrawal] = useState(0);
  const [levelWiseEarnings, setLevelWiseEarnings] = useState([]);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [myIdsSummary, setMyIdsSummary] = useState(null);
  const [coursePurchasePrompt, setCoursePurchasePrompt] = useState(null);
  const [courses, setCourses] = useState([]);
  const [courseStats, setCourseStats] = useState({
    total: 0,
    active: 0,
    optedOut: 0,
  });
  const [topReferrals, setTopReferrals] = useState([]);
  const [showLevelBreakdown, setShowLevelBreakdown] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);

      const results = await Promise.allSettled([
        userDashboardData(),
        userProfileData(),
        userMyCourses({ page: 1, per_page: 50 }),
        userWalletData({ page: 1, per_page: 1 }),
        userMyReferralList({ page: 1, per_page: 20 }),
      ]);

      const [dashRes, profileRes, coursesRes, walletRes, referralsRes] = results;

      if (dashRes.status === "fulfilled") {
        const data = dashRes.value?.data?.data || dashRes.value?.data || {};
        setReferralsCount(Number(data?.referrals_count ?? 0));
        setTotalEarnings(Number(data?.totalEarnings ?? data?.total_earnings ?? 0));
        setWalletAmount(Number(data?.wallet ?? 0));
        setLevelWiseEarnings(Array.isArray(data?.levelWiseEarnings) ? data.levelWiseEarnings : []);
        setRecentTransactions(
          Array.isArray(data?.recent_transactions)
            ? data.recent_transactions.map(mapTransaction)
            : []
        );
        setActivityLogs(Array.isArray(data?.activity_logs) ? data.activity_logs : []);
        setMyIdsSummary(data?.my_ids_summary || null);
        setCoursePurchasePrompt(data?.course_purchase_prompt || null);
      }

      if (profileRes.status === "fulfilled") {
        const profileData = profileRes.value?.data?.data || profileRes.value?.data || {};
        setProfile(profileData);
      }

      if (coursesRes.status === "fulfilled") {
        const responseData = coursesRes.value?.data || {};
        const courseData = responseData?.data || responseData || {};
        const list = Array.isArray(courseData?.courses)
          ? courseData.courses.map(mapCourse)
          : [];
        const paginationTotal = courseData?.pagination?.total ?? list.length;
        const active = list.filter(
          (c) => String(c.status).toLowerCase() === "success" && !c.opt_out_status
        ).length;
        const optedOut = list.filter((c) => c.opt_out_status || String(c.status).toLowerCase() === "opted_out").length;
        setCourses(list.slice(0, 20));
        setCourseStats({ total: paginationTotal, active, optedOut });
      }

      if (walletRes.status === "fulfilled") {
        const walletData = walletRes.value?.data?.data || {};
        const summary = walletData?.summary || {};
        setPendingWithdrawal(Number(summary?.pending_earnings ?? 0));
        if (!dashRes.status || dashRes.status !== "fulfilled") {
          setWalletAmount(Number(summary?.wallet_balance ?? summary?.available_balance ?? 0));
        }
      }

      if (referralsRes.status === "fulfilled") {
        const refData = referralsRes.value?.data?.data || referralsRes.value?.data || {};
        const refs = Array.isArray(refData?.referrals) ? refData.referrals.map(mapReferral) : [];
        setTopReferrals(refs.slice(0, 20));
        if (!dashRes.status || dashRes.status !== "fulfilled") {
          setReferralsCount(Number(refData?.pagination?.total ?? refs.length));
        }
      }

      setLoading(false);
    };

    fetchAll();
  }, []);

  const displayName = profile?.name || storedUser?.name || "User";
  const userId = profile?.custom_user_id || storedUser?.custom_user_id || storedUser?.id || "—";
  const referralNumber = profile?.referral_number || storedUser?.referral_number || "";
  const accountStatus = capitalize(profile?.status || storedUser?.status || "Active");
  const referralLink = buildReferralLink(referralNumber);

  const quickActions = useMemo(
    () => [
      { label: "Buy Course", icon: faShoppingCart, to: getCourseAccessPath(), primary: true },
      { label: "My Courses", icon: faBookOpen, to: Routes?.UserMyCourseList?.path || "/user-my-course-list" },
      { label: "Wallet", icon: faWallet, to: Routes?.UserWalletPage?.path || "/user-wallet-page" },
      { label: "Referrals", icon: faUsers, to: Routes?.UserLevelNetworkPage?.path || "/user-level-network-page" },
      { label: "Commission", icon: faChartLine, to: Routes?.UserLevelEarning?.path || "/user-level-earning" },
      { label: "Payments", icon: faFileInvoice, to: Routes?.UserMyPayments?.path || "/user-my-payments" },
      { label: "Network", icon: faSitemap, to: Routes?.UserLevelNetworkPage?.path || "/user-level-network-page" },
    ],
    []
  );

  const handleCopy = (label) => {
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="dashboard-page">
      <div className="d-flex justify-content-between flex-wrap align-items-center gap-3 mb-4 py-4">
        <div>
          <h2 className="h4 mb-1" style={{ color: "#111827" }}>Dashboard</h2>
          <p className="text-muted mb-0 small">Your courses, earnings, referrals, and activity at a glance.</p>
        </div>
        <Button as={Link} to={getCourseAccessPath()} style={ACTIVE_BUTTON_STYLE}>
          <FontAwesomeIcon icon={faArrowRight} className="me-2" />
          Browse Courses
        </Button>
      </div>

      <div className="dashboard-welcome mb-4">
        <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
          <div>
            <div className="dashboard-welcome-title">Welcome back, {displayName}</div>
            <div className="dashboard-welcome-meta d-flex flex-wrap">
              <span className="dashboard-meta-chip">
                <FontAwesomeIcon icon={faIdCard} />
                User ID: {userId}
              </span>
              {referralNumber && (
                <span className="dashboard-meta-chip">
                  Referral Code: {referralNumber}
                </span>
              )}
              <span className="dashboard-meta-chip">
                Status: {accountStatus}
              </span>
            </div>
          </div>
          {referralNumber && (
            <div className="d-flex flex-wrap gap-2">
              <CopyToClipboard text={referralNumber} onCopy={() => handleCopy("Referral code")}>
                <Button variant="outline-primary" size="sm" className="dashboard-quick-action-btn">
                  <FontAwesomeIcon icon={faCopy} className="me-1" />
                  Copy Code
                </Button>
              </CopyToClipboard>
              {referralLink && (
                <CopyToClipboard text={referralLink} onCopy={() => handleCopy("Referral link")}>
                  <Button variant="outline-primary" size="sm" className="dashboard-quick-action-btn">
                    <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                    Copy Link
                  </Button>
                </CopyToClipboard>
              )}
            </div>
          )}
        </div>
      </div>

      {coursePurchasePrompt?.show && (
        <DashboardSection
          className="mb-4"
          bodyClassName="p-4"
          title="Activate your earning potential"
          subtitle={coursePurchasePrompt.message}
          action={
            <Button as={Link} to={getCourseAccessPath()} style={ACTIVE_BUTTON_STYLE} size="sm">
              Browse Courses
            </Button>
          }
        >
          <span />
        </DashboardSection>
      )}

      {myIdsSummary && myIdsSummary.total_ids > 0 && (
        <DashboardSection
          className="mb-4"
          title={`My IDs (${myIdsSummary.total_ids})`}
          subtitle={`Active: ${myIdsSummary.active_ids} · Earning: ${myIdsSummary.ids_earning_commission} · Total earned: ${formatCurrency(myIdsSummary.total_earnings_all_ids)}`}
          action={
            <Button
              as={Link}
              to={Routes?.UserMyIds?.path || "/user-my-ids"}
              variant="outline-primary"
              size="sm"
            >
              View All
            </Button>
          }
        >
          <span />
        </DashboardSection>
      )}

      {loading ? (
        <LoadingSkeleton count={8} />
      ) : (
        <>
          <Row className="g-4 mb-4">
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Wallet Balance"
                subtitle="Available for withdrawal"
                value={formatCurrency(walletAmount, { decimals: 2 })}
                icon={faWallet}
                tone="primary"
                footer={
                  <InfoTip
                    label="What is wallet?"
                    tip="Your wallet holds commission earnings available for withdrawal."
                  />
                }
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Total Commission"
                subtitle="Lifetime earnings"
                value={formatCurrency(totalEarnings)}
                icon={faIndianRupee}
                tone="success"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Direct Referrals"
                subtitle="Users referred by you"
                value={referralsCount}
                icon={faUsers}
                tone="info"
                footer={
                  <InfoTip
                    label="What is downline?"
                    tip="Direct referrals are users who joined using your referral code."
                  />
                }
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Pending Withdrawal"
                subtitle="Awaiting processing"
                value={formatCurrency(pendingWithdrawal, { decimals: 2 })}
                icon={faHourglassHalf}
                tone="warning"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Courses Purchased"
                subtitle="Total enrolled courses"
                value={courseStats.total}
                icon={faBookOpen}
                tone="dark"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Active Courses"
                subtitle="Currently enrolled"
                value={courseStats.active}
                icon={faCheckCircle}
                tone="success"
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Opted Out"
                subtitle="Courses opted out"
                value={courseStats.optedOut}
                icon={faTimesCircle}
                tone="danger"
                footer={
                  <InfoTip
                    label="What is opt-out?"
                    tip="Opt-out lets you cancel enrollment during the enrollment window."
                  />
                }
              />
            </Col>
            <Col xs={12} sm={6} xl={3}>
              <DashboardStatCard
                title="Commission Levels"
                subtitle="Levels with earnings"
                value={levelWiseEarnings.length || 0}
                icon={faChartLine}
                tone="primary"
              />
            </Col>
          </Row>

          <DashboardSection
            className="mb-4"
            title="Quick Actions"
            subtitle="Jump to common tasks"
          >
            <div className="dashboard-quick-actions">
              {quickActions.map((action) => (
                <Button
                  key={action.label}
                  as={Link}
                  to={action.to}
                  variant={action.primary ? undefined : "outline-primary"}
                  style={action.primary ? ACTIVE_BUTTON_STYLE : undefined}
                  className="dashboard-quick-action-btn"
                >
                  <FontAwesomeIcon icon={action.icon} className="me-2" />
                  {action.label}
                </Button>
              ))}
            </div>
          </DashboardSection>

          <Row className="g-4 mb-4 align-items-stretch">
            <Col xs={12} xl={7} className="d-flex">
              <DashboardSection
                className="w-100"
                uniform
                title="My Courses"
                subtitle="Recent course enrollment status"
                action={
                  <Button
                    as={Link}
                    to={Routes?.UserMyCourseList?.path || "/user-my-course-list"}
                    variant="link"
                    size="sm"
                    className="p-0"
                  >
                    View all
                  </Button>
                }
              >
                {courses.length === 0 ? (
                  <EmptyState
                    icon={faBookOpen}
                    title="No courses yet"
                    message="Purchase a course to start your learning and earning journey."
                  />
                ) : (
                  <div className="dashboard-section-scroll">
                    <Table hover className="dashboard-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Course</th>
                          <th>Status</th>
                          <th>Opt-out Deadline</th>
                          <th>Period</th>
                        </tr>
                      </thead>
                      <tbody>
                        {courses.map((course) => (
                          <tr key={course.id}>
                            <td className="fw-semibold">{course.title}</td>
                            <td>
                              <Badge
                                bg={getCourseStatusBadge(course.status, course.opt_out_status, course.is_final)}
                                className="px-2 py-1 rounded-pill"
                              >
                                {getCourseStatusLabel(course)}
                              </Badge>
                            </td>
                            <td>{formatDate(course.opt_out_deadline)}</td>
                            <td>{course.month_year || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </DashboardSection>
            </Col>

            <Col xs={12} xl={5} className="d-flex">
              <DashboardSection
                className="w-100"
                uniform
                title="Commission Snapshot"
                subtitle="Level-wise earnings summary"
                action={
                  levelWiseEarnings.length > 0 && (
                    <Button
                      variant="link"
                      size="sm"
                      className="p-0"
                      onClick={() => setShowLevelBreakdown((prev) => !prev)}
                    >
                      <FontAwesomeIcon icon={showLevelBreakdown ? faChevronUp : faChevronDown} className="me-1" />
                      {showLevelBreakdown ? "Collapse" : "Expand"}
                    </Button>
                  )
                }
              >
                {levelWiseEarnings.length === 0 ? (
                  <EmptyState
                    icon={faChartLine}
                    title="No commission yet"
                    message="Commission will appear here when your network makes purchases."
                  />
                ) : showLevelBreakdown ? (
                  <>
                    <div className="dashboard-section-scroll">
                      {levelWiseEarnings.map((item) => (
                        <div key={item.level} className="dashboard-level-row">
                          <span>{item.level}</span>
                          <span className="fw-bold">{formatCurrency(item.total)}</span>
                        </div>
                      ))}
                    </div>
                    <div className="dashboard-section-scroll-footer d-flex justify-content-between pt-3 border-top fw-bold">
                      <span>Total</span>
                      <span>{formatCurrency(totalEarnings)}</span>
                    </div>
                  </>
                ) : (
                  <div className="text-muted small py-2">
                    Total earned: <strong>{formatCurrency(totalEarnings)}</strong> across {levelWiseEarnings.length} levels.
                  </div>
                )}
              </DashboardSection>
            </Col>
          </Row>

          <Row className="g-4 mb-4 align-items-stretch">
            <Col xs={12} md={6} className="d-flex">
              <DashboardSection
                className="w-100"
                uniform
                title="Referral Snapshot"
                subtitle="Your direct referral network"
                action={
                  <Button
                    as={Link}
                    to={Routes?.UserLevelNetworkPage?.path || "/user-level-network-page"}
                    variant="link"
                    size="sm"
                    className="p-0"
                  >
                    View all
                  </Button>
                }
              >
                <div className="mb-3 flex-shrink-0">
                  <span className="text-muted small">Direct referrals: </span>
                  <strong>{referralsCount}</strong>
                  {referralNumber && (
                    <div className="mt-2 text-muted small">
                      Referral code: <strong>{referralNumber}</strong>
                    </div>
                  )}
                </div>
                {topReferrals.length === 0 ? (
                  <EmptyState
                    icon={faUsers}
                    title="No referrals yet"
                    message="Share your referral code to grow your network."
                  />
                ) : (
                  <div className="dashboard-section-scroll">
                    <Table className="dashboard-table align-middle mb-0">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Status</th>
                          <th>Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topReferrals.map((ref) => (
                          <tr key={ref.id}>
                            <td className="fw-semibold">{ref.name}</td>
                            <td>
                              <Badge bg={ref.status === "active" ? "success" : "secondary"} className="rounded-pill px-2 py-1">
                                {capitalize(ref.status)}
                              </Badge>
                            </td>
                            <td>{formatDate(ref.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </DashboardSection>
            </Col>

            <Col xs={12} md={6} className="d-flex">
              <DashboardSection
                className="w-100"
                uniform
                title="Recent Activity"
                subtitle="Latest updates on your account"
              >
                {activityLogs.length === 0 ? (
                  <EmptyState
                    title="No recent activity"
                    message="Course purchases, commissions, and withdrawals will show here."
                  />
                ) : (
                  // <div className="dashboard-section-scroll">
                    <div className="dashboard-section-scroll" style={{ maxHeight: "20rem" }}>
                    {activityLogs.map((item) => (
                      <div key={item.id} className="dashboard-activity-item d-flex justify-content-between gap-3">
                        <div>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Badge bg={getActivityBadge(item.type)} className="rounded-pill px-2 py-1">
                              {capitalize(item.type || "Update")}
                            </Badge>
                            <span className="dashboard-activity-time">{item.time}</span>
                          </div>
                          <div className="dashboard-activity-title">{item.title}</div>
                          {item.message && (
                            <div className="dashboard-activity-message">{item.message}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </DashboardSection>
            </Col>
          </Row>

          <DashboardSection
            title="Recent Transactions"
            subtitle="Latest payments and wallet entries"
            action={
              <Button
                as={Link}
                to={Routes?.UserMyPayments?.path || "/user-my-payments"}
                variant="link"
                size="sm"
                className="p-0"
              >
                View all
              </Button>
            }
          >
            {recentTransactions.length === 0 ? (
              <EmptyState
                icon={faMoneyBillWave}
                title="No transactions yet"
                message="Your payment and wallet history will appear here."
              />
            ) : (
              <div className="table-responsive">
                <Table hover className="dashboard-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th className="text-end">Amount</th>
                      <th>Method</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentTransactions.map((item) => (
                      <tr key={item.id}>
                        <td className="fw-semibold">{capitalize(item.type)}</td>
                        <td
                          className={`text-end fw-bold ${
                            item.type === "credit" ? "text-success" : "text-danger"
                          }`}
                        >
                          {item.type === "credit" ? "+" : "−"}
                          {formatCurrency(Math.abs(item.amount))}
                        </td>
                        <td>{item.payment_method}</td>
                        <td className="text-center">
                          <Badge bg={getPaymentStatusBadge(item.status)} className="px-2 py-1 rounded-pill">
                            {capitalize(item.status)}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </DashboardSection>
        </>
      )}
    </div>
  );
}
